import { cn } from '@/lib/utils.ts'
import { cva } from 'class-variance-authority'
import { AlarmClock, CheckCheck, MonitorOff, PauseCircle, StopCircle } from 'lucide-react'
import ALogoSpinner from '@/components/atoms/ALogoSpinner.tsx'

const statusVariants = cva('text-xs rounded-lg px-3.5 py-2 inline-flex items-center gap-2 w-fit', {
  variants: {
    status: {
      WAITING_FOR_FEEDBACK: 'text-orange-600 bg-orange-100',
      AWAITING: 'text-orange-600 bg-orange-100',
      DEFAULT: 'text-stone-600 bg-stone-100',
      STOPPED: 'text-stone-600 bg-stone-100',
      WORKING: 'text-violet-600 bg-violet-100',
      RUNNING: 'text-violet-600 bg-violet-100',
      PAUSED: 'text-stone-600 bg-stone-200',
      DONE: 'text-green-600 bg-green-100',
      PAUSING: 'text-stone-600 bg-stone-200',
      DEAD_VM: 'text-reddish-600 bg-reddish-100',
      FAILED: 'text-reddish-600 bg-reddish-100',
    },
  },
  defaultVariants: {
    status: 'DEFAULT',
  },
})

const IconComponents: Record<Status, React.ComponentType<{ className?: string }>> = {
  WORKING: ALogoSpinner,
  RUNNING: ALogoSpinner,
  WAITING_FOR_FEEDBACK: AlarmClock,
  AWAITING: AlarmClock,
  PAUSED: PauseCircle,
  DONE: CheckCheck,
  PAUSING: PauseCircle,
  STOPPED: StopCircle,
  DEFAULT: ALogoSpinner,
  DEAD_VM: MonitorOff,
  FAILED: StopCircle,
}

// it's not VM, iteration, or any other status. It's all of it rolled into one.
export default function MIterationStatusPill({
  status,
  className,
  short = false,
}: {
  status: Status
  className?: string
  short?: boolean
}) {
  if (!status) {
    // console.warn('MIterationStatusPill: status is undefined!', status)
    return null // handle broken projects. Normally it should never happen.
  }
  const knownStatus = IconComponents[status] ? status : 'DEFAULT'

  const Icon = IconComponents[knownStatus]
  const labelMapping = short ? shortStatusToLabel : statusToLabel
  const label = labelMapping[status] ?? status.replace(/_/g, ' ')
  return (
    <div className={cn(statusVariants({ status: knownStatus }), className)}>
      <Icon className="size-4" />
      {label}
    </div>
  )
}

type Status =
  | 'WORKING'
  | 'RUNNING'
  | 'WAITING_FOR_FEEDBACK'
  | 'DONE'
  | 'PAUSED'
  | 'PAUSING'
  | 'AWAITING'
  | 'DEFAULT'
  | 'STOPPED'
  | 'DEAD_VM'
  | 'FAILED'

const statusToLabel: Record<Status, string> = {
  AWAITING: 'Waiting for feedback',
  WORKING: 'Proofs is working',
  RUNNING: 'Proofs is working',
  WAITING_FOR_FEEDBACK: 'Waiting for feedback',
  PAUSED: 'Paused',
  DONE: 'Done',
  PAUSING: 'Pausing...',
  DEFAULT: 'Initializing',
  STOPPED: 'Stopped',
  DEAD_VM: 'Not Running',
  FAILED: 'Failed',
}

const shortStatusToLabel: Record<Status, string> = {
  AWAITING: 'Waiting',
  WORKING: 'Working',
  RUNNING: 'Working',
  WAITING_FOR_FEEDBACK: 'Waiting',
  PAUSED: 'Paused',
  DONE: 'Done',
  PAUSING: 'Pausing',
  DEFAULT: 'Initializing',
  STOPPED: 'Stopped',
  DEAD_VM: 'Not Running',
  FAILED: 'Failed',
}
